import { MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, message, Modal } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import CreditTypesApiService from '../../../../api/CreditTypesApiService';
import AuthorizedContent from '../../../../components/AuthorizedContent';
import CreditTypeDetailsForm from '../../../../components/forms/postSecondary/CreditTypeDetailsForm';
import EditCancelButton from '../../../../components/buttons/EditCancelButton';
import Breadcrumbs from '../../../../config/Breadcrumbs';
import Routes from '../../../../config/Routes';
import FeatureFlag from '../../../../consts/FeatureFlag';
import * as DeleteCreditTypeHandler from '../../../../handlerModels/DeleteCreditTypeHandler';
import * as GetCreditTypeDetailsHandler from '../../../../handlerModels/GetCreditTypeDetailsHandler';
import CreditTypeDTO from '../../../../models/CreditTypeDTO';
import HeaderPortal from '../../../../portals/HeaderPortal';
import BaseFormState from '../../../../redux/bases/BaseFormState';
import Guid from '../../../../utils/Guid';
import HistoryUtil from '../../../../utils/HistoryUtil';
import PageStayPrompt from '../../../../utils/PageStayPrompt';
import ParameterUtil from '../../../../utils/ParameterUtil';

interface CreditTypeDetailsState extends BaseFormState {
  creditTypeId: string;
  creditType?: CreditTypeDTO;
}

interface CreditTypeDetailsProps {
}

class CreditTypeDetailsPage extends React.Component<CreditTypeDetailsProps & RouteComponentProps<RouteObject>, CreditTypeDetailsState> {
  private readonly _creditTypeDetailsRef = React.createRef<CreditTypeDetailsForm>();

  constructor(props: CreditTypeDetailsProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      creditTypeId: Guid.Empty(),
    };
  }

  componentDidMount() {
    const creditTypeId = ParameterUtil.getPathPart(this.props.match, 'id');

    if (creditTypeId == 'new') {
      this.setState({ isEditing: true, isNew: true, loading: false });
    }
    else {
      this.loadCreditType(creditTypeId);
    }
  }

  private loadCreditType = (creditTypeId: string) => {
    this.setState({ creditTypeId: creditTypeId });

    CreditTypesApiService.getCreditTypeDetails(creditTypeId)
      .then((results: GetCreditTypeDetailsHandler.Result) => {
        if (results.creditType) {
          this.setState({ creditType: results.creditType });
        }
      }).catch(() => {
        this.setState({ error: true });
      }).finally(() => {
        this.setState({ loading: false });
      });
  }

  private toggleEdit = () => {
    if (this.state.isEditing) {
      this._creditTypeDetailsRef.current?.resetForm();
    }

    this.setState({ isEditing: !this.state.isEditing });
  }

  private creditTypeDetailsSaved = (creditTypeId: string) => {
    this.setState({ isEditing: false });
    if (creditTypeId != this.state.creditTypeId) {
      HistoryUtil.replace(Routes.generate(Routes.CREDIT_TYPE_DETAILS, { id: creditTypeId }));
    }
    else {
      this.loadCreditType(creditTypeId);
    }
  }

  private deleteConfirmation = (id: string) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this Credit Type?',
      okText: 'Delete',
      onOk: () => this.deleteCreditType(id)
    });
  }

  private deleteCreditType = (creditTypeId: string) => {
    this.setState({ submitting: true });

    const request = DeleteCreditTypeHandler.Request.create({
      creditTypeId: creditTypeId,
    });

    CreditTypesApiService.deleteCreditType(request)
      .then((result: DeleteCreditTypeHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          message.success('Credit Type was removed.');
        }
        else {
          message.error(result?.errors.join('\n'));
        }

      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Credit Type could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToListing();
      });
  }

  goToListing = () => {
    HistoryUtil.goBack();
  }

  render() {
    const title = this.state.loading ? '...' : this.state.isNew ? 'New Credit Type' : this.state.creditType?.name ?? '...';
    return (
      <Content >
        <HeaderPortal
          title={title}
          onBack={this.goToListing}
          breadcrumbs={Breadcrumbs.creditTypeDetails(title, this.state.creditTypeId)}
          extra={this.renderMoreActions()} />
        <PageStayPrompt when={this.state.isEditing} />

        {this.renderDetailsCard()}
      </Content >
    );
  }

  renderDetailsCard() {
    const editButton = this.state.creditTypeId == Guid.Empty() ?
      null :
      (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_CREDIT_TYPE]}>
          <EditCancelButton isEditing={this.state.isEditing} onClick={this.toggleEdit} />
        </AuthorizedContent>
      );

    return (
      <Card title="Details" extra={editButton} >
        <CreditTypeDetailsForm
          ref={this._creditTypeDetailsRef}
          creditTypeId={this.state.creditTypeId}
          isEditing={this.state.isEditing}
          isNew={this.state.isNew}
          onSave={this.creditTypeDetailsSaved} />
      </Card>
    );
  }

  renderMoreActions() {
    const menu = (
      <Menu>
        < Menu.Item title='Delete Credit Type' key={this.state.creditTypeId} onClick={() => this.deleteConfirmation(this.state.creditTypeId)}  >
          Delete Credit Type
        </Menu.Item>
      </Menu>
    );
    return (
      <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_CREDIT_TYPE]}>
        <Dropdown overlay={menu}>
          <Button size='large' type='link' >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </AuthorizedContent>
    );
  }
}

export default withRouter(CreditTypeDetailsPage);
